// #region Imports
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import Tooltip from "@mui/material/Tooltip";
import EntityMember from "_shared/entity-members/entity-member";
import Typography from "@mui/material/Typography";
import EditFeeScheduleCategory from "_shared/fee-schedules/categories/_views/edit-fee-schedule-category";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import { FeeScheduleCategoryMaskedCurrency, MaskedFeeSchedule } from "work/masked-fields";
// #endregion

// #region Styled Components
const Drawer = styled(MuiDrawer)(() => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    height: 'calc(100% - 64px)',
    top: '64px',
    width: SIDE_PANEL_WIDTH,
  },
}));
const FeeCategoryPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition"
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    width: open ? SIDE_PANEL_WIDTH : 0,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open
        ? theme.transitions.easing.easeOut
        : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}));
const FeeCategoryPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
  minHeight: 0,
}));
const FeeCategoryPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  padding: theme.spacing(2),
  // Fills the paper rather than restating its width, which would put the padding outside the panel.
  boxSizing: "border-box",
  width: "100%",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
// #endregion

// #region Props/Types
type EditFeeScheduleCategoryDrawerProps = {
  category?: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  formKey?: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCategoryAdded?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) => void;
  selectedCategories?: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  teamMembers?: readonly EntityMember[];
  /** See `EditFeeScheduleCategory`. A template passes true, because it is a rate card. */
  teamMemberIsOptional?: boolean;
  /** See `EditFeeScheduleCategory`. Reaches people who are on no list this surface holds. */
  onBrowseForTeamMember?: () => Promise<EntityMember | undefined>;
  fieldErrors?: Readonly<Record<string, string>>;
};
// #endregion

export default function EditFeeScheduleCategoryDrawer(props: Readonly<EditFeeScheduleCategoryDrawerProps>) {
  const {
    category,
    formKey,
    isOpen,
    onClose,
    onCategoryAdded,
    onCategoryUpdated,
    selectedCategories,
    teamMembers,
    fieldErrors,
    teamMemberIsOptional,
    onBrowseForTeamMember,
  } = props;

  // #region Render
  return (
    <FeeCategoryPanel open={isOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
      <FeeCategoryPanelContainer>
        <FeeCategoryPanelContent>
          <TitleBar>
            <Typography variant="h5">{category ? "Edit Category" : "Create New Category"}</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          <EditFeeScheduleCategory
            key={category?.id.value ?? formKey ?? "new"}
            category={category}
            onCancel={onClose}
            onCategoryAdded={onCategoryAdded}
            onCategoryUpdated={onCategoryUpdated}
            selectedCategories={selectedCategories}
            teamMembers={teamMembers}
            fieldErrors={fieldErrors}
            teamMemberIsOptional={teamMemberIsOptional}
            onBrowseForTeamMember={onBrowseForTeamMember}
          />
        </FeeCategoryPanelContent>
      </FeeCategoryPanelContainer>
    </FeeCategoryPanel>
  );
  // #endregion
}
